// The home Stempeluhr's project choice: what to book the open working time on
// once clocked in, the way Zeit's Start screen offers it ("Woran arbeitest du?",
// time-ui StartProjectChooser). Pure read-side shaping of three time-api reads
// (`GET /projects`, `GET /activities`, `GET /timer`); the writes live in
// useClockProjects. Ported, not imported: services stay self-contained (ADR 0001).

export interface ClockProject {
  id: string;
  name: string;
  /** The project's own default Tätigkeit, else the workspace's. */
  defaultActivityId: string | null;
}

export interface ClockActivity {
  id: string;
  name: string;
}

/** The project timer running inside the open block. */
export interface RunningProject {
  projectId: string;
  /** The server's name for it -- the project may no longer be in her bookable list. */
  projectName: string | null;
  activityId: string | null;
  startedAt: string;
}

interface ProjectRow {
  id?: unknown;
  name?: unknown;
  status?: unknown;
  isMember?: unknown;
  effectiveActivityId?: unknown;
}

interface ActivityRow {
  id?: unknown;
  name?: unknown;
  isActive?: unknown;
}

function asList(data: unknown): unknown[] {
  if (Array.isArray(data)) return data;
  const inner = (data as { data?: unknown } | null | undefined)?.data;
  return Array.isArray(inner) ? inner : [];
}

// Only what the server would accept (time-api project-booking-rules.ts): an open
// project she is a member of. A project manager's own read lists every project
// she manages, staffed on it or not -- `isMember: false` is not bookable for her.
// Sorted by name, as Zeit's chooser is.
export function bookableProjects(data: unknown): ClockProject[] {
  return asList(data)
    .filter((row): row is ProjectRow => !!row && typeof row === 'object')
    .filter((row) => typeof row.id === 'string' && typeof row.name === 'string')
    .filter((row) => row.status !== 'closed' && row.isMember !== false)
    .map((row) => ({
      id: row.id as string,
      name: row.name as string,
      defaultActivityId: typeof row.effectiveActivityId === 'string' ? row.effectiveActivityId : null,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

// Active Tätigkeiten in the server's order.
export function activeActivities(data: unknown): ClockActivity[] {
  return asList(data)
    .filter((row): row is ActivityRow => !!row && typeof row === 'object')
    .filter((row) => typeof row.id === 'string' && typeof row.name === 'string' && row.isActive !== false)
    .map((row) => ({ id: row.id as string, name: row.name as string }));
}

// `GET /timer`: a project timer (`kind: 'timer'` with a project), else nothing --
// a bare clock-in (`kind: 'clock'`) runs no project.
export function runningProjectOf(data: unknown): RunningProject | null {
  const row = data as { kind?: unknown; projectId?: unknown; projectName?: unknown; activityId?: unknown; startedAt?: unknown } | null;
  if (!row || row.kind !== 'timer' || typeof row.projectId !== 'string') return null;
  return {
    projectId: row.projectId,
    projectName: typeof row.projectName === 'string' ? row.projectName : null,
    activityId: typeof row.activityId === 'string' ? row.activityId : null,
    startedAt: typeof row.startedAt === 'string' ? row.startedAt : '',
  };
}

// The running timer's Tätigkeit, else the one the server resolves when none is
// given (the project's effective default).
export function runningActivityId(running: RunningProject | null, projects: ClockProject[]): string | null {
  if (!running) return null;
  return running.activityId ?? projects.find((p) => p.id === running.projectId)?.defaultActivityId ?? null;
}

// One tap or two: with more than one Tätigkeit to choose from the picker asks
// which; with one there is nothing to ask, and with none the server resolves it.
export function needsActivityStep(activities: ClockActivity[]): boolean {
  return activities.length > 1;
}

// The same dot colour Zeit gives the project (time-ui utils/projectColor.ts): one
// pure hash over the id into the categorical palette, so a project reads as the
// same colour on Home and in Zeit.
const CHART_VARS = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
  'var(--chart-6)',
  'var(--chart-7)',
  'var(--chart-8)',
];

export function projectColorVar(projectId: string | null | undefined): string {
  const value = String(projectId ?? '');
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) hash = (hash * 31 + value.charCodeAt(i)) >>> 0;
  return CHART_VARS[hash % CHART_VARS.length];
}

// A search field only once the list no longer fits a phone screen.
export const PROJECT_SEARCH_FROM = 8;

export function filterProjects(projects: ClockProject[], query: string): ClockProject[] {
  const q = query.trim().toLocaleLowerCase();
  return q ? projects.filter((p) => p.name.toLocaleLowerCase().includes(q)) : projects;
}
